import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { QUERY_KEYS } from "@/lib/constants";
import type {
  DashboardReport,
  SalesReport,
  ProductReport,
  PaymentReport,
} from "@/types";

const ALWAYS_FROM_SERVER = {
  staleTime: 0,
  gcTime: 0,
  refetchOnMount: "always",
  refetchOnWindowFocus: true,
} as const;

interface ReportParams {
  startDate?: string;
  endDate?: string;
  [key: string]: string | undefined;
}

export function useDashboardReport(params?: ReportParams) {
  return useQuery({
    queryKey: [
      ...QUERY_KEYS.dashboardReport,
      params?.startDate,
      params?.endDate,
    ],
    queryFn: () =>
      apiClient.get<DashboardReport>("/reports/dashboard", params),
    ...ALWAYS_FROM_SERVER,
  });
}

export function useSalesReport(params?: ReportParams) {
  return useQuery({
    queryKey: QUERY_KEYS.salesReport(params?.startDate, params?.endDate),
    queryFn: () => apiClient.get<SalesReport>("/reports/sales", params),
    ...ALWAYS_FROM_SERVER,
  });
}

export function useProductReport(params?: ReportParams) {
  return useQuery({
    queryKey: QUERY_KEYS.productReport(params?.startDate, params?.endDate),
    queryFn: () => apiClient.get<ProductReport[]>("/reports/products", params),
    ...ALWAYS_FROM_SERVER,
  });
}

export function usePaymentReport(params?: ReportParams) {
  return useQuery({
    queryKey: QUERY_KEYS.paymentReport(params?.startDate, params?.endDate),
    queryFn: () => apiClient.get<PaymentReport>("/reports/payments", params),
    ...ALWAYS_FROM_SERVER,
  });
}

export function useEndOfDayReport(date?: string) {
  return useQuery({
    queryKey: QUERY_KEYS.endOfDayReport(date),
    queryFn: () =>
      apiClient.get<DashboardReport>("/reports/end-of-day", { date }),
    ...ALWAYS_FROM_SERVER,
  });
}
